import type { Producto } from '../types'
import { normalizar } from './format'

/** La presentación en el formato que ya usa el catálogo: "KG" para un
 *  kilo, los gramos tal cual ("500"), y el resto con su unidad. */
function presentacion(peso: number, unidad: Producto['unidad']): string {
  if (!peso) return ''
  const n = String(peso)
  switch (unidad) {
    case 'kg':
      // Medio kilo se escribe en gramos (500), como el resto del catálogo.
      if (peso < 1) return String(Math.round(peso * 1000))
      return peso === 1 ? 'KG' : `${n}KG`
    case 'g':
      return n
    case 'litro':
      return peso === 1 ? 'LT' : `${n}LT`
    default:
      return `${n}${unidad.slice(0, 2).toUpperCase()}`
  }
}

/** El SKU que se le propone a un producto nuevo, con la misma forma que
 *  los que ya existen: tres letras del nombre y la presentación
 *  —"Dátiles 500 g" → DAT-500, "Mix berries 1 kg" → MIX-KG—.
 *
 *  Si choca con uno que ya existe, prueba sumando la segunda palabra
 *  (MAN-TOS-KG) y, si aun así choca, un número al final. El SKU es único
 *  en la base (`productos_codigo_unico`), así que proponer uno repetido
 *  solo serviría para que el guardado falle. */
export function sugerirSku(
  producto: Pick<Producto, 'id' | 'nombre' | 'peso' | 'unidad'>,
  existentes: Pick<Producto, 'id' | 'codigo'>[],
): string {
  const palabras = normalizar(producto.nombre)
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((p) => p.length > 1)
  if (!palabras.length) return ''

  const usados = new Set(
    existentes.filter((e) => e.id !== producto.id).map((e) => e.codigo.toUpperCase()),
  )
  const pres = presentacion(producto.peso, producto.unidad)
  const armar = (...partes: string[]) => partes.filter(Boolean).join('-')

  const candidatos = [
    armar(palabras[0].slice(0, 3).toUpperCase(), pres),
    palabras[1] ? armar(palabras[0].slice(0, 3).toUpperCase(), palabras[1].slice(0, 3).toUpperCase(), pres) : '',
  ].filter(Boolean)

  const libre = candidatos.find((c) => !usados.has(c))
  if (libre) return libre

  let n = 2
  while (usados.has(`${candidatos[0]}-${n}`)) n++
  return `${candidatos[0]}-${n}`
}
